<aside class="container">
  <header class="container__header">
    @if (showCancelButtons.fromHeader) {
      <ts-button [type]="'icon'" [icon]="'close'" [color]="'accent'" iconColor="white" (click)="cancel()"></ts-button>
    }
    @if (showFormTitle) {
      <div class="container__header__title">{{ getDrawerDynamicTitle() }}</div>
    }
    @if (canEditLayout) {
      <div class="container__header__actions" [appAuthorize]="getContextPermission()">
        @if (ui().isEditing) {
          <ts-button
            [type]="'icon'"
            [icon]="'remove_circle'"
            [color]="'accent'"
            iconColor="white"
            matTooltip="Cancel editing"
            (click)="toggleEditing()"
          ></ts-button>
          <ts-button
            [type]="'icon'"
            [icon]="'check_circle'"
            [color]="'accent'"
            iconColor="white"
            matTooltip="Save changes"
            [disabled]="!canSaveTemplate"
            (click)="saveTemplate()"
          ></ts-button>
        } @else {
          @if (enableEditing) {
            <ts-button
              [type]="'icon'"
              [icon]="'edit'"
              [color]="'accent'"
              iconColor="white"
              matTooltip="Start editing"
              (click)="toggleEditing()"
            ></ts-button>
          }
          @if (enableAiButton) {
            <button aria-label="AI Ability" class="ai-button" (click)="invokeAbility()">
              <p>Ask Ethan AI</p>
              @if (!ui().isAiLoading) {
                <img src="assets/icons/ai-shine-stars-yellow.svg" class="ai-button-icon" alt="AI Ability" />
              } @else {
                <mat-spinner diameter="20" class="ai-button-spinner"></mat-spinner>
              }
            </button>
          }
        }
      </div>
    }
  </header>

  <mat-progress-bar mode="indeterminate" [ngClass]="{ hidden: !ui().isLoading }"></mat-progress-bar>

  <main
    class="container__field-list"
    cdkDropList
    [cdkDropListData]="currentFields"
    (cdkDropListDropped)="dropped($event)"
    cdkDropListOrientation="vertical"
  >
    @for (field of currentFields; let fieldIndex = $index; let isLastField = $last; track field.name) {
      <section
        class="container__field-list__item"
        [class.placeholder]="field.type === 'placeholder'"
        cdkDrag
        [cdkDragDisabled]="!ui().isEditing"
      >
        <div class="container__field-list__item__left">
          @if (ui().isEditing) {
            <ts-icon class="cursor-pointer" [icon]="'drag_indicator'" matTooltip="Drag me" cdkDragHandle> </ts-icon>
          }
        </div>
        <div class="container__field-list__item__center">
          @switch (field.type) {
            @case ('fieldDef') {
              <app-form-field
                [fieldDef]="field.fieldDef"
                [model]="setValues"
                [data]="formFieldData"
                [readonly]="ui().isAiLoading"
                [alwaysHideHistory]="true"
                [isRequired]="field.isRequired"
                (onUpdate)="onFieldUpdate($event)"
                [doNotEvaluateChanges]="true"
              ></app-form-field>
            }
            @case ('placeholder') {
              <mat-form-field>
                <mat-label>Select a field</mat-label>
                <mat-select (selectionChange)="onFieldSelected($event)">
                  @for (field of allowedFields; track field.name) {
                    <mat-option [disabled]="isSelected(field.name)" [value]="field">
                      {{ field?.label }}
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>
            }
            @default {
              @switch (field.name) {
                @case (relatedToType === relatedToTypeEnum.Project ? 'title' : 'name') {
                  <app-custom-items-name
                    [relatedToType]="relatedToType"
                    (onNavigateByUrl)="navigateByUrl($event)"
                    (onValueChanged)="onFieldUpdate($event)"
                    (onToggleLoading)="onToggleLoading()"
                    [commonData]="commonData"
                    [setValues]="setValues"
                    [readonly]="ui().isAiLoading"
                  ></app-custom-items-name>
                }
                @case ('cff_address') {
                  <app-custom-address
                    [relatedToType]="relatedToType"
                    (onToggleLoading)="onToggleLoading()"
                    (onNavigateByUrl)="navigateByUrl($event)"
                    (onValueChanged)="onFieldUpdate($event)"
                    [setValues]="setValues"
                    [readonly]="ui().isAiLoading"
                  ></app-custom-address>
                }
                @case ('template_id') {
                  <app-custom-deal-layout
                    [dataSource]="dataSources['template_id']"
                    (onValueChanged)="onFieldUpdate($event)"
                    [readonly]="ui().isAiLoading"
                  ></app-custom-deal-layout>
                }
                @case ('cff_add_properties') {
                  <app-custom-add-properties
                    (onValueChanged)="onFieldUpdate($event)"
                    [setValues]="setValues"
                    [readonly]="ui().isAiLoading"
                  ></app-custom-add-properties>
                }
                @case ('tag_list') {
                  <app-custom-tag-list
                    [relatedToType]="relatedToType"
                    (onValueChanged)="onFieldUpdate($event)"
                    [readonly]="ui().isAiLoading"
                  ></app-custom-tag-list>
                }
                @case ('category_name') {
                  <app-custom-contact-category
                    (onValueChanged)="onFieldUpdate($event)"
                    [readonly]="ui().isAiLoading"
                  ></app-custom-contact-category>
                }
                @case ('address_string') {
                  <app-custom-address-string
                    (onValueChanged)="onFieldUpdate($event)"
                    [setValues]="setValues"
                    [readonly]="ui().isAiLoading"
                  ></app-custom-address-string>
                }
                @case ('company_name') {
                  <app-custom-associate-company
                    (onValueChanged)="onFieldUpdate($event)"
                    [readonly]="ui().isAiLoading"
                  ></app-custom-associate-company>
                }
              }
            }
          }
        </div>
        <div class="container__field-list__item__right">
          @if (ui().isEditing) {
            @if (!field.isRequired || field.isRemovable) {
              <ts-button
                [type]="'icon'"
                [icon]="'remove_circle'"
                [color]="'accent'"
                iconColor="black"
                [matTooltip]="field.type === 'placeholder' ? 'Cancel' : 'Remove field'"
                (click)="removeField(fieldIndex)"
              ></ts-button>
            }
            @if (isLastField && field.type !== 'placeholder' && !ui().isAddingField) {
              <ts-button
                [type]="'icon'"
                [icon]="'add_circle'"
                [color]="'accent'"
                iconColor="black"
                [matTooltip]="'Add field'"
                (click)="addField()"
              ></ts-button>
            }
            @if (field.type === 'placeholder') {
              <ts-button
                [type]="'icon'"
                [icon]="'check_circle'"
                [color]="'accent'"
                iconColor="black"
                [disabled]="!selectedFieldDef"
                [matTooltip]="'Save field'"
                (click)="saveField(fieldIndex)"
              ></ts-button>
            }
          }
        </div>
      </section>
    }
  </main>

  <footer
    class="container__footer"
    [ngClass]="{
      'justify-end': !renderAttachFileField,
      'justify-between': renderAttachFileField,
    }"
  >
    @if (renderAttachFileField) {
      <section class="container__footer__file-upload">
        <app-custom-file-upload (onClearFile)="onClearFile()" (onFileSelected)="onFieldUpdate($event)">
        </app-custom-file-upload>
      </section>
    }
    <section class="container__footer__actions">
      @if (showCancelButtons.fromFooter) {
        <ts-button variant="secondary" (click)="cancel()" class="mr-2">Cancel</ts-button>
      }
      @if (!commonData.callbackData) {
        <ts-button
          data-cy="deal-save-button"
          variant="primary"
          [disabled]="isSaveButtonDisabled"
          [debounceTime]="200"
          (click)="save()"
        >
          Save
        </ts-button>
      } @else {
        <ts-button [matMenuTriggerFor]="menu" variant="primary" [disabled]="isSaveButtonDisabled" [menuIcon]="true">
          Save
        </ts-button>
        <mat-menu #menu>
          <button mat-menu-item id="create-project" (click)="save()">Single Deal</button>
          <button
            mat-menu-item
            [disabled]="!canCreatePortfolio"
            [ngClass]="{ 'cursor-not-allowed': !canCreatePortfolio }"
            (click)="save(commonData.callbackData)"
          >
            Portfolio
          </button>
        </mat-menu>
      }
    </section>
  </footer>
</aside>
